import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';
import {OiamEvents, TokenEnsurer} from '@oiambk/oiam-oauth-types';
import {BehaviorSubject, delay, filter, from, fromEvent, map, Observable, of, take, tap} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {APP_BASE_HREF} from "@angular/common";

@Injectable({
    providedIn: 'root'
})
export class OiamService {
    private readonly _basePath = inject(APP_BASE_HREF);
    token = new BehaviorSubject<string | undefined>(undefined);
    isLoggedOut$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    tokenEnsurer = new TokenEnsurer();

    constructor() {
        if (!environment.oiam.enabled) {
            return;
        }

        this.subscribeToEvents();
    }

    public get validToken() {
        const token = this.token.value;
        if (!token) {
            return of(token);
        }

        return from(this.tokenEnsurer.ensureValidToken(token));
    }


    subscribeToEvents() {
        this.subscribeToLoadedEvent();
        this.subscribeToReadyForLoginEvent();
        this.subscribeToStateEvent();
        this.subscribeToTokenEvent();
        this.subscribeToErrorEvent();
    }

    // Setzt die Konfiguration, sobald die Lib geladen ist
    subscribeToLoadedEvent() {
        fromEvent(globalThis, OiamEvents.LOADED)
            .pipe(
                take(1)
            )
            .subscribe(() => this.setConfig());
    }

    setConfig() {
        const config: any = {
            clientid: environment.oiam.clientId,
            redirecturi: globalThis.location.origin + this._basePath,
            customstate: globalThis.location.pathname.slice(this._basePath.length)
                + globalThis.location.search
                + globalThis.location.hash,
            authority: environment.oiam.authority,
            actingOnBehalfOf: ['urn:bafor:step:', 'urn:bafor:kiwi:'],
            deprecatedExpireEvents: false
        };

        globalThis.dispatchEvent(
            new CustomEvent(OiamEvents.SETCONFIG, {detail: config})
        );
    }

    // Triggert den Login, sobald die Lib dafür bereit ist
    subscribeToReadyForLoginEvent() {
        fromEvent(globalThis, OiamEvents.READYFORLOGIN)
            .pipe(
                filter(() => !this.isLoginFlowCompleted()),
                take(1)
            )
            .subscribe(() => this.doLogin());
    }

    doLogin() {
        console.log('dispatch Event: doLogin');
        globalThis.dispatchEvent(new CustomEvent(OiamEvents.DOLOGIN));
    }
    
    subscribeToStateEvent() {
        fromEvent<CustomEvent>(globalThis, OiamEvents.CUSTOMSTATE)
            .pipe(
                map(e => e.detail?.customstate as string | undefined),
                filter(Boolean),
                take(1)
            )
            .subscribe(customState => {
                const newUrl = `${globalThis.location.origin}${this._basePath}${customState}`;
                if (newUrl !== globalThis.location.href) {
                    globalThis.location.replace(newUrl);
                }
            });
    }

    subscribeToTokenEvent() {
        // Speichert das erhaltene JWT
        fromEvent(globalThis, OiamEvents.TOKEN)
            .pipe(
                map((event: any) => event.detail?.jwt),
                filter((token) => !!token)
            )
            .subscribe((token) => {
                this.token.next(token);
            });

        // Erkennt, wenn der User in einem anderen Tab ausgeloggt wird - dann müssen wir uns selbst um den Redirect kümmern
        fromEvent(globalThis, OiamEvents.TOKEN)
            .pipe(
                map((event: any) => event.detail?.jwt),
                filter((token) => !!this.token.value && !token),
                take(1),
                tap(() => this.isLoggedOut$.next(true)),
                delay(1000)
            )
            .subscribe(() => {
                globalThis.location.href = environment.oiam.logoutUri;
            });
    }

    subscribeToErrorEvent() {
        fromEvent(globalThis, OiamEvents.ERROR)
            .pipe(take(1))
            .subscribe(() =>
                globalThis.location.href = environment.oiam.logoutUri
            );
    }


    isLoginFlowCompleted() {
        return !!this.token.value;
    }

    //
    static canActivate(_childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<string> | Observable<boolean> {
        if (!environment.oiam.enabled) {
            return true;
        }
        return inject(OiamService).canActivateRoute(state.url);
    }

    private static isReturningFromLogin() {
        const searchParams = new URLSearchParams(globalThis.location.search);
        return searchParams.has('code') && searchParams.has('state');
    }

    canActivateRoute(routerPath: string) {
        if (this.isLoginFlowCompleted()) {
            return true;
        }

        if (OiamService.isReturningFromLogin()) {
            return this.waitForToken();
        } else {
            return this.waitForToken().pipe(map(() => true));
        }
    }

    waitForToken() {
        return this.token.pipe(
            filter((token) => !!token),
            take(1)
        );
    }

}